import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getNotificationPreferences,
  sendNotificationTestEmail,
  updateNotificationPreferences,
} from "../../api/notificationPreferencesApi";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "never", label: "Never" },
];

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        const body = await getNotificationPreferences();
        if (!cancelled) {
          setPreferences(body.preferences);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error.message || "Unable to load notification preferences.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = (field) => (event) => {
    setPreferences((current) => ({
      ...current,
      [field]: event.target.checked,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const body = await updateNotificationPreferences(preferences);
      setPreferences(body.preferences);
      setSuccessMessage("Notification preferences saved.");
    } catch (error) {
      if (error.status === 428 && error.body?.missingRequiredConsents?.length) {
        navigate("/account/consent-required", {
          state: { returnTo: "/account/notifications" },
        });
        return;
      }

      setErrorMessage(error.message || "Unable to save preferences.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setIsSendingTest(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const body = await sendNotificationTestEmail();
      setSuccessMessage(body.message || "Test email sent.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to send test email.");
    } finally {
      setIsSendingTest(false);
    }
  };

  if (isLoading || !preferences) {
    return (
      <section className="panel-card">
        <p className="muted">Loading notification preferences…</p>
      </section>
    );
  }

  return (
    <section className="panel-card">
      <h2>Notifications</h2>
      <p className="muted">
        Control email alerts for saved searches and favorite listings. Marketing emails are
        separate from these account alerts.
      </p>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
      {successMessage ? <p className="admin-users-success">{successMessage}</p> : null}

      <form className="admin-users-form" onSubmit={handleSubmit}>
        <fieldset className="admin-users-fieldset">
          <legend>Email notifications</legend>
          <label className="admin-users-checkbox">
            <input
              type="checkbox"
              checked={preferences.emailNotifications}
              onChange={handleToggle("emailNotifications")}
            />
            Email notifications (master toggle)
          </label>
          <label className="admin-users-checkbox">
            <input
              type="checkbox"
              checked={preferences.savedSearchAlerts}
              onChange={handleToggle("savedSearchAlerts")}
            />
            Saved search alerts
          </label>
          <label className="admin-users-checkbox">
            <input
              type="checkbox"
              checked={preferences.favoriteListingAlerts}
              onChange={handleToggle("favoriteListingAlerts")}
            />
            Favorite listing alerts
          </label>
          <label className="admin-users-checkbox">
            <input
              type="checkbox"
              checked={preferences.priceChangeAlerts}
              onChange={handleToggle("priceChangeAlerts")}
            />
            Price change alerts
          </label>
          <label className="admin-users-checkbox">
            <input
              type="checkbox"
              checked={preferences.listingStatusAlerts}
              onChange={handleToggle("listingStatusAlerts")}
            />
            Listing status alerts
          </label>
          <label className="admin-users-field">
            <span>Alert frequency</span>
            <select
              value={preferences.alertFrequency}
              onChange={(event) =>
                setPreferences((current) => ({
                  ...current,
                  alertFrequency: event.target.value,
                }))
              }
            >
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <div className="admin-users-form__actions">
          <button type="submit" className="report-submit-cta" disabled={isSaving}>
            {isSaving ? "Saving…" : "Save preferences"}
          </button>
        </div>
      </form>

      <section className="account-security-section">
        <h3>Test email</h3>
        <p className="muted">
          Sends a test notification if your email is verified and email notifications are enabled.
        </p>
        <button
          type="button"
          className="midpoint-share-button"
          onClick={handleTestEmail}
          disabled={isSendingTest}
        >
          {isSendingTest ? "Sending…" : "Send test email"}
        </button>
      </section>

      <section className="account-security-section">
        <h3>Saved search alerts</h3>
        <p className="muted">
          You can also enable or disable alerts per saved search on the{" "}
          <Link to="/account/saved-searches">Saved Searches</Link> page.
        </p>
      </section>
    </section>
  );
};

export default NotificationsPage;
